import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FaBars, 
  FaTimes, 
  FaUser, 
  FaShoppingCart, 
  FaSignOutAlt,
  FaHistory,
  FaMapMarkerAlt,
  FaChevronDown,
  FaSearch,
  FaRuler
} from 'react-icons/fa';

import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoriesContext';
import ProfilePicture from './ProfilePicture';
import StyleCreditsDisplay from './StyleCreditsDisplay';
import './Header.css';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isCollectionsDropdownOpen, setIsCollectionsDropdownOpen] = useState(false);
  const [logoImage, setLogoImage] = useState('');
  const [logoLoading, setLogoLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartItemCount } = useCart();
  const { currentUser, logout } = useAuth();
  const { categories } = useCategories();
  const profileDropdownRef = useRef(null);
  const collectionsDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (collectionsDropdownRef.current && !collectionsDropdownRef.current.contains(event.target)) {
        setIsCollectionsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const closeProfileDropdown = () => {
    setIsProfileDropdownOpen(false);
  };

  const toggleCollectionsDropdown = () => {
    setIsCollectionsDropdownOpen(!isCollectionsDropdownOpen);
  };

  const closeCollectionsDropdown = () => {
    setIsCollectionsDropdownOpen(false);
  };

  const handleCollectionClick = (categoryId) => {
    navigate(`/collections?category=${categoryId}`);
    closeCollectionsDropdown();
    closeMobileMenu();
  };

  // Sort categories by name
  const sortedCategories = [...categories].sort((a, b) => {
    const nameA = (a.name || a.categoryName || a.id || '').toLowerCase();
    const nameB = (b.name || b.categoryName || b.id || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
    closeProfileDropdown();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return 'U';
  };

    // Use Firebase Storage logo with responsive sizes
  const fetchLogo = useCallback(async () => {
    try {
      setLogoLoading(true);
      
      // Use Firebase Storage logo with responsive sizes
      const logoUrls = {
        '480w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_480w.webp',
        '768w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_768w.webp',
        '1080w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1080w.webp',
        '1440w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1440w.webp',
        '1920w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1920w.webp'
      };
      
      // Check if Firebase logo exists, otherwise fallback to local webp
      const img = new Image();
      img.onload = () => {
        setLogoImage(logoUrls['1080w']); // Use 1080w as default
        setLogoLoading(false);
      };
      img.onerror = () => {
        // Fallback to local webp logo if Firebase fails
        setLogoImage('/images/logo/hero_desktop_1756135482771_0_1080w.webp');
        setLogoLoading(false);
      };
      img.src = logoUrls['1080w'];
      
    } catch (error) {
      setLogoImage('/images/logo/hero_desktop_1756135482771_0_1080w.webp'); // Use local webp fallback if anything fails
      setLogoLoading(false);
    }
  }, []);

  // Fetch logo when component mounts
  useEffect(() => {
    fetchLogo();
  }, [fetchLogo]);

  return (
    <header className="header">
      <div className="header-content">
        {/* Left Side - Logo and Brand */}
        <div className="header-left">
          <Link to="/" className="header-logo" onClick={closeMobileMenu}>
            {logoLoading ? null : logoImage ? (
              <img
                src={logoImage}
                alt="Vastré By Vijh"
                className="header-logo-image"
                loading="eager"
                                  onError={(e) => {
                    e.target.src = '/images/logo/hero_desktop_1756135482771_0_1080w.webp';
                  }}
              />
            ) : (
              <img
                src="/images/logo/hero_desktop_1756135482771_0_1080w.webp"
                alt="Vastré By Vijh"
                className="header-logo-image"
                loading="eager"
              />
            )}
          </Link>
        </div>

        {/* Center - Navigation Links */}
        <nav className="nav-desktop">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
          >
            Home
          </Link>
          <Link 
            to="/collections" 
            className={`nav-link ${isActive('/collections') ? 'active' : ''}`}
          >
            Collections
          </Link>
          <Link 
            to="/bespoke-consultation" 
            className={`nav-link ${isActive('/bespoke-consultation') ? 'active' : ''}`}
          >
            Bespoke Consultation
          </Link>
          <Link 
            to="/how-to-measure" 
            className={`nav-link ${isActive('/how-to-measure') ? 'active' : ''}`}
          >
            How to Measure
          </Link>
          <Link 
            to="/about" 
            className={`nav-link ${isActive('/about') ? 'active' : ''}`}
          >
            About
          </Link>
          <Link 
            to="/contact" 
            className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
          >
            Contact
          </Link>
        </nav>

        {/* Right Side - Shopping Cart and Profile */}
        <div className="header-right">
          {/* Track Order - Only show for guests */}
          {!currentUser && (
            <Link to="/track-order" className="track-order-btn">
              <FaSearch />
            </Link>
          )}

          {/* Shopping Cart */}
          <Link to="/cart" className="cart-icon">
            <div className="cart-icon-container">
              <FaShoppingCart className="cart-icon-svg" />
              {getCartItemCount() > 0 && (
                <div className="cart-count-badge">
                  <span className="cart-count-number">{getCartItemCount()}</span>
                </div>
              )}
            </div>
          </Link>

          {/* Style Credits Display - Show for all users */}
          <StyleCreditsDisplay />

          {/* Profile Avatar with Dropdown */}
          <div className="profile-section" ref={profileDropdownRef}>
            {currentUser ? (
              <button className="profile-avatar" onClick={toggleProfileDropdown}>
                <ProfilePicture
                  photoURL={currentUser.photoURL}
                  userId={currentUser.uid}
                  alt={currentUser.displayName || currentUser.email}
                  className="profile-avatar-image"
                  fallbackInitials={getUserInitials()}
                  size="medium"
                />
                <FaChevronDown className={`profile-dropdown-arrow ${isProfileDropdownOpen ? 'rotated' : ''}`} />
              </button>
            ) : (
              <Link to="/login" className="profile-avatar guest">
                <FaUser className="profile-avatar-icon" />
              </Link>
            )}

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && currentUser && (
              <div className="profile-dropdown">
                {/* User Info Header */}
                <div className="dropdown-user-info">
                  <ProfilePicture
                    photoURL={currentUser.photoURL}
                    userId={currentUser.uid}
                    alt={currentUser.displayName || currentUser.email}
                    className="dropdown-user-image"
                    fallbackInitials={getUserInitials()}
                    size="medium"
                  />
                  <div className="dropdown-user-details">
                    <h4 className="dropdown-user-name">
                      {currentUser.displayName || 'User'}
                    </h4>
                    <p className="dropdown-user-email">
                      {currentUser.email}
                    </p>
                  </div>
                </div>

                {/* Dropdown Navigation */}
                <div className="dropdown-nav">
                  <Link to="/order-history" className="dropdown-item" onClick={closeProfileDropdown}>
                    <FaHistory className="dropdown-icon" />
                    <span>Order History</span>
                  </Link>
                  
                  <Link to="/addresses" className="dropdown-item" onClick={closeProfileDropdown}>
                    <FaMapMarkerAlt className="dropdown-icon" />
                    <span>Saved Addresses</span>
                  </Link>
                  
                  <Link to="/saved-measurements" className="dropdown-item" onClick={closeProfileDropdown}>
                    <FaRuler className="dropdown-icon" />
                    <span>Saved Measurements</span>
                  </Link>
                </div>

                {/* Logout Section */}
                <div className="dropdown-logout">
                  <button onClick={handleLogout} className="dropdown-logout-btn">
                    <FaSignOutAlt className="dropdown-icon" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop for closing menu */}
          <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
          
          <nav className="nav-mobile">
            {/* Top Row - Close Button Only */}
            <div className="mobile-menu-top">
              <button className="mobile-menu-icon mobile-close-btn" onClick={closeMobileMenu}>
                <FaTimes />
              </button>
            </div>
            
            {/* Navigation Links */}
            <div className="mobile-menu-links">
              <Link 
                to="/" 
                className="nav-link" 
                onClick={closeMobileMenu}
              >
                Home
              </Link>
              
              {/* Collections Dropdown */}
              <div className="mobile-collections-dropdown" ref={collectionsDropdownRef}>
                <button
                  className={`nav-link mobile-collections-toggle ${isCollectionsDropdownOpen ? 'active' : ''}`}
                  onClick={toggleCollectionsDropdown}
                >
                  <span className="mobile-collections-text">Collections</span>
                  <span className="mobile-collections-icon-wrapper">
                    <FaChevronDown className={`mobile-collections-arrow ${isCollectionsDropdownOpen ? 'rotated' : ''}`} />
                  </span>
                </button>
                
                {isCollectionsDropdownOpen && (
                  <div className="mobile-collections-menu">
                    <button
                      className={`mobile-collection-item ${location.pathname === '/collections' && !location.search ? 'active' : ''}`}
                      onClick={() => {
                        navigate('/collections');
                        closeCollectionsDropdown();
                        closeMobileMenu();
                      }}
                    >
                      All Collections
                    </button>
                    {sortedCategories.map((category) => {
                      const categoryName = category.name || category.categoryName || category.id;
                      const isActive = location.pathname === '/collections' && 
                                       location.search.includes(`category=${category.id}`);
                      return (
                        <button
                          key={category.id}
                          className={`mobile-collection-item ${isActive ? 'active' : ''}`}
                          onClick={() => handleCollectionClick(category.id)}
                        >
                          {categoryName}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <Link 
                to="/bespoke-consultation" 
                className="nav-link" 
                onClick={closeMobileMenu}
              >
                Bespoke Consultation
              </Link>
              <Link 
                to="/how-to-measure" 
                className="nav-link" 
                onClick={closeMobileMenu}
              >
                How to Measure
              </Link>
              <Link 
                to="/about" 
                className="nav-link" 
                onClick={closeMobileMenu}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="nav-link" 
                onClick={closeMobileMenu}
              >
                Contact
              </Link>
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header; 